import { parsedUrlQueryToRecord } from '@porterin/booking-flow/src/order-review/domain/entities/OrderReviewClientData';
import {
  feResultPageSensitiveParams,
  orderReviewPageSensitiveParams,
  feResultsParamsIdentifier,
  orderReviewParamsIdentifier,
} from '@porterin/component-library/src/ts-utils/analytics/configs/FeParamsConfig';
import { FeParamsPage } from '@porterin/component-library/src/ts-utils/analytics/configs/FeParamsPage';
import { getAppConfig } from '@porterin/component-library/src/ts-utils/analytics/fetch-config/FetchConfig';
import { captureException } from '@porterin/component-library/src/ts-utils/analytics/sentry/Sentry';
import {
  encryptAES,
  decryptAES,
} from '@porterin/component-library/src/utils/crypto/Crypto';
import {
  getURLParams,
  getSearchParams,
  createRequestURLfrmParams,
  getHostName,
  getPathSplitParts,
} from '@porterin/lead-flow/src/domain/configs/Config';
import { NextRouter } from 'next/router';

// Moves specified URL parameters to Storage and encrypts them
export const moveSelectParamsToStore = async (
  url: string,
  paramKeys: string[],
  identifier: string,
): Promise<void> => {
  const payload: Record<string, string> = {};
  const params = getURLParams(getSearchParams(url));

  paramKeys.forEach(paramKey => {
    const val = params[paramKey];
    if (val) {
      payload[paramKey] = decodeURIComponent(val);
    }
  });

  const encryptedCookie = await encryptAES(
    JSON.stringify(payload) || '',
    getAppConfig().cookie_encryption_secret,
  );

  localStorage.setItem(identifier, encryptedCookie);
};

// Removes specified parameters from a URL and returns the cleaned URL
export const removeSelectParamsFromUrl = (
  url: string,
  paramKeys: string[],
): string => {
  const params = getURLParams(getSearchParams(url));
  const filteredParams: Record<string, string> = Object.fromEntries(
    Object.entries(params).filter(([key]) => !paramKeys.includes(key)),
  );
  const decodedParams = Object.fromEntries(
    Object.entries(filteredParams).map(([key, value]) => [
      key,
      decodeURIComponent(value),
    ]),
  );

  return createRequestURLfrmParams(
    ``,
    `${new URL(url, 'https://dummy.com').pathname}`, // host doesnt matter, this is for pathname extraction
    decodedParams,
  );
};

// Retrieves and decrypts data from Storage, returning it as a key-value object
export const getParsedDataFromStore = async (
  identifier: string,
): Promise<Record<string, string>> => {
  const encryptedData = localStorage.getItem(identifier);
  if (!encryptedData)
    throw new Error('No data found in localStorage for ' + identifier);

  const decryptedData = await decryptAES(
    encryptedData,
    getAppConfig().cookie_encryption_secret,
  );
  return JSON.parse(decryptedData);
};

// Saves sensitive parameters to Storage and redirects to a cleaned URL
export const saveToStoreAndRedirect = (
  url: string,
  page: FeParamsPage,
): void => {
  const [params, identifier] =
    page === FeParamsPage.FareEstimateResults
      ? [feResultPageSensitiveParams, feResultsParamsIdentifier]
      : [orderReviewPageSensitiveParams, orderReviewParamsIdentifier];

  moveSelectParamsToStore(url, params, identifier)
    .then(
      () =>
        (window.location.href = `${window.location.origin}${removeSelectParamsFromUrl(url, params)}`),
    )
    .catch(error => {
      captureException(
        `Error in saveToStoreAndRedirect - ${error instanceof Error ? error.message : String(error)}`,
      );
    });
};

// Retrieves parameters from Storage and merges them with current router query parameters
export const getUpdatedParamsFromStore = async (
  router: NextRouter,
  page: FeParamsPage,
  cb: (params: Record<string, string | undefined>) => void,
): Promise<void> => {
  const identifier =
    page === FeParamsPage.FareEstimateResults
      ? feResultsParamsIdentifier
      : orderReviewParamsIdentifier;

  const updatedParams = parsedUrlQueryToRecord(router.query);

  try {
    const parsedData = await getParsedDataFromStore(identifier);
    Object.keys(parsedData).forEach(key => {
      const val = parsedData[key];
      if (val) {
        updatedParams[key] = val;
      }
    });
    cb(updatedParams);
  } catch (error) {
    captureException(
      `Error in getUpdatedParamsFromStore - ${error instanceof Error ? error.message : String(error)}`,
    );
    throw error;
  }
};
